/* eslint-disable react-hooks/rules-of-hooks */
"use client";
import Image from "next/image";
import React, { useState, useEffect } from "react";
import Manuals from "./main-section/manuals/Manuals";
import { useSearchParams } from "next/navigation";
import {
  MdArrowCircleRight,
  MdArrowCircleLeft,
  MdKeyboardArrowRight,
  MdKeyboardArrowDown,
} from "react-icons/md";
import Link from "next/link";
import MachineSpecificGallery from "@/app/_components/machines/MachineSpecificGallery";
import SizeChanger from "./SizeChanger";

export default function MainSection({ data, machineDemoImages }) {
  const { decks, main_image, manuals, name, style, new_description } = data;
  const [showManuals, setShowManuals] = useState(false);
  const searchParams = useSearchParams();
  const receivedDeckType = searchParams.get("deckType");
  const receivedDeckSize = searchParams.get("deckSize");
  const seenTypes = new Set();
  let defaultDeck;

  const [selectedDeck, setSelectedDeck] = useState(
    defaultDeck || decks[0] || {}
  );
  const [thumbNails, setThumbNails] = useState(
    selectedDeck.thumbs?.data || selectedDeck.image?.data || []
  );
  const [currentThumbIndex, setCurrentThumbIndex] = useState(0);
  const handleArrowClick = (direction) => {
    let newIndex = currentThumbIndex;
    if (direction === "left") {
      newIndex =
        currentThumbIndex === 0 ? thumbNails.length - 1 : currentThumbIndex - 1;
    } else if (direction === "right") {
      newIndex =
        currentThumbIndex === thumbNails.length - 1 ? 0 : currentThumbIndex + 1;
    }
    setCurrentThumbIndex(newIndex);
    const url =
      thumbNails.length > 0 ? thumbNails[newIndex].attributes.url : null;
    const newSelectedDeck = {
      ...selectedDeck,
      image: {
        data: {
          attributes: {
            url: url,
          },
        },
      },
    };
    setSelectedDeck(newSelectedDeck);
  };

  if (!data) return <div>Loading...</div>;

  if (receivedDeckSize && receivedDeckType) {
    defaultDeck = decks.find(
      (deck) =>
        deck.type.data.attributes.type === receivedDeckType &&
        deck.size === receivedDeckSize
    );
  } else {
    defaultDeck = decks[0] || {};
  }

  const uniqueDecks = decks.filter((deck) => {
    const type = deck.type.data.attributes.type;
    if (seenTypes.has(type)) {
      return false; // Skip adding this deck to `uniqueDecks` if its type was already encountered
    } else {
      seenTypes.add(type);
      return true; // Include this deck in `uniqueDecks` if its type is new
    }
  });

  useEffect(() => {
    setSelectedDeck(defaultDeck || decks[0] || {});
    setThumbNails(defaultDeck?.thumbs?.data || defaultDeck?.image?.data || []);
  }, [data]);

  const handleSelectedDeckType = (type) => {
    const deck = decks.find((deck) => deck.type.data.attributes.type === type);
    setSelectedDeck(deck);
    setThumbNails(deck.thumbs.data || deck.image.data);
  };
  const handleSelectedFloorMachine = (machine) => {
    setSelectedDeck(machine);
  };
  let selectedDeckLabel = selectedDeck.label;

  // Remove "inch - " or "Inch" from the label
  selectedDeckLabel = selectedDeckLabel
    ?.replace(/inch - /gi, "")
    .replace(/Inch/gi, "");

  return (
    <>
      <div className="mobile-machine-page">
        <div className="mobile-machine-deck-options">
          {decks.length > 1 && (
            <>
              <h2>Choose Your Deck</h2>
              {decks.map((deck) => (
                <div
                  className="deck-option"
                  key={deck.id}
                  onClick={() => handleSelectedFloorMachine(deck)}
                >
                  {deck.type.data.attributes.type === "None" ? (
                    <span>{deck.name}</span>
                  ) : (
                    <span>
                      {deck.type.data.attributes.type.replace(" - 3b", "")}
                    </span>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
        <div className="mobile-machine-image-wrapper">
          <div className="arrows">
            {thumbNails.length > 1 && (
              <>
                <div
                  className="left-arrow"
                  onClick={() => {
                    handleArrowClick("left");
                  }}
                >
                  <MdArrowCircleLeft />
                </div>
                <div
                  className="right-arrow"
                  onClick={() => {
                    handleArrowClick("right");
                  }}
                >
                  <MdArrowCircleRight />
                </div>
              </>
            )}
          </div>
          <Image
            src={
              selectedDeck.image
                ? selectedDeck.image.data.attributes.url
                : main_image.data.attributes.url
            }
            alt="Main Image"
            width={1920}
            height={1080}
          />
        </div>
        {/* sibling of the image wrapper, not a child: that wrapper carries the
            absolutely-positioned `.arrows` overlay across its full box, which
            would swallow clicks on the ladder buttons. */}
        <SizeChanger data={data} />
        <div className="mobile-machine-general-data">
          <h2>
            {selectedDeck.name}{" "}
            {selectedDeck.type.data.attributes.type === "None" ? (
              <span></span>
            ) : (
              <span>
                {selectedDeck.type.data.attributes.type.replace(" - 3b", "")}
              </span>
            )}
          </h2>
          <h4>{style.data.attributes.style}</h4>
        </div>
        <div className="mobile-machine-description">
          {new_description?.map((description) => (
            <p key={description.id}>{description.paragraph}</p>
          ))}

          <p>{selectedDeck.description_one}</p>
          <p>{selectedDeck?.description_two}</p>
        </div>
        <div className="mobile-machine-cta-button-wrapper">
          <Link href="#demo">Get a Free Demo</Link>
        </div>
        <div className="mobile-machine-manuals">
          <div
            className="manuals-header"
            onClick={() => setShowManuals(!showManuals)}
          >
            {showManuals ? <MdKeyboardArrowDown /> : <MdKeyboardArrowRight />}

            <h3>Manuals</h3>
          </div>
          {showManuals && <Manuals data={manuals} />}
        </div>
      </div>
      <div className="notepad-machine-page">
        <div className="notepad-machine-image-thumbs-wrapper">
          <div className="notepad-machine-image-wrapper">
            <Image
              src={
                selectedDeck.image
                  ? selectedDeck.image.data.attributes.url
                  : main_image.data.attributes.url
              }
              alt="Main Image"
              width={1920}
              height={1080}
            />
          </div>
          {thumbNails.length > 1 && (
            <div className="notepad-machine-thumbs">
              {thumbNails?.map((thumb, index) => (
                <div
                  key={thumb.id}
                  className={`thumb ${index === currentThumbIndex ? "selected" : ""
                    }`}
                  onClick={() => {
                    setCurrentThumbIndex(index);
                    const url = thumb.attributes.url;
                    const newSelectedDeck = {
                      ...selectedDeck,
                      image: {
                        data: {
                          attributes: {
                            url: url,
                          },
                        },
                      },
                    };
                    setSelectedDeck(newSelectedDeck);
                  }}
                >
                  <Image
                    src={thumb.attributes.url}
                    alt="Thumb"
                    width={1920}
                    height={1080}
                  />
                </div>
              ))}
            </div>
          )}
          <SizeChanger data={data} />
        </div>
        <div className="notepad-machine-general-data">
          <div className="notepad-machine-title">
            <div>
              <h2>
                {selectedDeck.name}{" "}
                {selectedDeck.type.data.attributes.type === "None" ||
                  selectedDeck.type.data.attributes.type === "Sweeper" ? (
                  <span></span>
                ) : (
                  <span>
                    {selectedDeck.type.data.attributes.type.replace(
                      " - 3b",
                      ""
                    )}
                  </span>
                )}
              </h2>
              <h4>{style.data.attributes.style}</h4>
            </div>
            <Link href="#demo">Get a Free Demo</Link>
          </div>

          <div className="notepad-machine-manuals">
            <Manuals data={manuals} />
          </div>
          <div className="notepad-machine-description">
            {new_description?.map((description) => (
              <p key={description.id}>{description.paragraph}</p>
            ))}
            <p>{selectedDeck?.description_one}</p>
            <p>{selectedDeck?.description_two}</p>
          </div>
          {uniqueDecks.length > 1 ? (
            <div className="notepad-deck-options">
              {uniqueDecks.map((deck) => (
                <div
                  className="notepad-deck-option"
                  key={deck.id}
                  onClick={() =>
                    handleSelectedDeckType(deck.type.data.attributes.type)
                  }
                >
                  <Image
                    src={deck.type.data.attributes.image.data.attributes.url}
                    width={1920}
                    height={1080}
                    alt={deck.type.data.attributes.type}
                  />
                  <span>
                    {deck.type.data.attributes.type.replace(" - 3b", "")}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="notepad-deck-options">
              {decks.length > 1 &&
                decks.map((deck) => (
                  <div
                    className="notepad-deck-option"
                    key={deck.id}
                    onClick={() => handleSelectedFloorMachine(deck)}
                  >
                    <Image
                      src={deck.image.data.attributes.url}
                      width={1920}
                      height={1080}
                      alt={deck.type.data.attributes.type}
                    />
                    <span>{deck.name}</span>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
      {machineDemoImages && machineDemoImages.length > 5 && (
        <MachineSpecificGallery data={machineDemoImages} />
      )}
    </>
  );
}
